/**
 * PreviewInRoomButton - try a variant's texture in the visualizer, saved or not.
 *
 * A freshly generated (or uploaded) texture lives only in the editor until the
 * product is saved, and the visualizer knows saved products only. So the
 * texture is sent as a preview (a temporary product the API keeps a day, see
 * pms-api-v2 texture-preview) and the visualizer opens on its SKU.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2, ScanEye } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { config } from '../../../../../../common/hooks/use-settings-api.hook';
import { buildVisualizerUrl, PREVIEW_LINK_ATTRIBUTION } from '../../../../../../common/utils/visualizer-link';
import { createTexturePreview } from '../../../api/productV2Api';
import type { VariantV3 } from '../../../types';

interface PreviewInRoomButtonProps {
  variant: VariantV3;
  /** The texture shown for the variant: a data/blob URL when not saved yet, else its URL. */
  textureUrl?: string;
}

/**
 * The unsaved texture as a file. A generated one is a data URL, which the
 * page's CSP (connect-src has no `data:`) will not let fetch() read, so it is
 * decoded here; an uploaded one is a blob URL, which fetch() may read.
 */
async function textureBlob(url: string): Promise<Blob> {
  const match = /^data:([^;,]+)?(;base64)?,([\s\S]*)$/.exec(url);
  if (!match) return (await fetch(url)).blob();
  const [, type = 'image/jpeg', base64, data] = match;
  if (!base64) return new Blob([decodeURIComponent(data)], { type });
  const bytes = atob(data);
  const out = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i += 1) out[i] = bytes.charCodeAt(i);
  return new Blob([out], { type });
}

export const PreviewInRoomButton: React.FC<PreviewInRoomButtonProps> = ({ variant, textureUrl }) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const params = variant.visualization?.surface?.seamlessParams;
  const width = Number(params?.textureWidth) || 0;
  const height = Number(params?.textureHeight) || 0;
  if (!textureUrl) return null;
  const ready = width > 0 && height > 0;

  // The visualizer puts a product on the first surface it declares (floor
  // before walls), so the preview declares only the surface asked for.
  const open = async (surface: 'floor' | 'walls') => {
    setError(null);
    // Opened now, while the click still counts (a tab opened after an await is blocked).
    const tab = window.open('about:blank', '_blank');
    setBusy(true);
    try {
      const saved = /^https?:\/\//.test(textureUrl);
      const texture = saved ? undefined : await textureBlob(textureUrl);
      const preview = await createTexturePreview({
        texture,
        materialPath: saved ? textureUrl.split('?')[0] : undefined,
        textureWidth: width,
        textureHeight: height,
        variantUuid: variant.uuid,
        product: { axes: variant.axes as Record<string, unknown>, organization: { applicationTypes: [surface] } },
      });
      const alias = preview.alias || config?.alias;
      const url = alias ? buildVisualizerUrl({ alias, sku: preview.sku, attribution: PREVIEW_LINK_ATTRIBUTION }) : '';
      if (!url) throw new Error(t('PAGES.NEW_PRODUCT.V3.PREVIEW_IN_ROOM_FAILED'));
      if (tab) tab.location.href = url;
      else window.open(url, '_blank');
    } catch (err: any) {
      tab?.close();
      setError(err?.message || t('PAGES.NEW_PRODUCT.V3.PREVIEW_IN_ROOM_FAILED'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={!ready || busy}>
          <Button variant="outline" size="sm" className="w-full max-w-[180px]"
            title={ready ? undefined : t('PAGES.NEW_PRODUCT.V3.PREVIEW_IN_ROOM_NEEDS_SIZE')}>
            {busy ? <Loader2 className="animate-spin" /> : <ScanEye />}
            {t('PAGES.NEW_PRODUCT.V3.PREVIEW_IN_ROOM')}
            <ChevronDown className="ms-auto opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => open('floor')}>{t('PAGES.NEW_PRODUCT.V3.PREVIEW_ON_FLOOR')}</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => open('walls')}>{t('PAGES.NEW_PRODUCT.V3.PREVIEW_ON_WALLS')}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {!ready && <span className="max-w-[180px] text-xs text-muted-foreground">{t('PAGES.NEW_PRODUCT.V3.PREVIEW_IN_ROOM_NEEDS_SIZE')}</span>}
      {error && <span className="max-w-[180px] text-xs text-destructive">{error}</span>}
    </div>
  );
};

export default PreviewInRoomButton;
